import { useCallback, useState } from "react";
import { Shell } from "@/modules/shell/Shell";
import { MobileNav } from "@/modules/shell/MobileNav";
import { TAB_TITLES, type AppTab } from "@/modules/shell/tabs";
import { AuthPanel } from "@/modules/auth/AuthPanel";
import { WalletsPanel } from "@/modules/wallet/WalletsPanel";
import { HomePanel } from "@/modules/home/HomePanel";
import { ShopsPanel } from "@/modules/shops/ShopsPanel";
import { OraclePanel } from "@/modules/oracle/OraclePanel";
import { PayPanel } from "@/modules/pay/PayPanel";
import { MarketPanel } from "@/modules/market/MarketPanel";
import { InboxPanel } from "@/modules/inbox/InboxPanel";
import { PwaUpdateBanner } from "@/pwa/PwaUpdateBanner";
import { getNetworkEnv, type NetworkEnv } from "@/network/env";
import { SessionProvider, useSession } from "@/session/SessionContext";
import { ToastProvider, useToast } from "@/toast/ToastContext";

function AppInner() {
  const { session, logout } = useSession();
  const toast = useToast();
  const [networkEnv, setNetwork] = useState<NetworkEnv>(() => getNetworkEnv());
  const [tab, setTab] = useState<AppTab>("home");

  const sessionOn = !!session;

  const go = useCallback(
    (t: AppTab) => {
      if (!sessionOn && t !== "auth" && t !== "home" && t !== "market" && t !== "trade") {
        setTab("auth");
        return;
      }
      setTab(t);
    },
    [sessionOn]
  );

  const navTab: AppTab =
    tab === "inbox" || tab === "shops" || tab === "oracle" || tab === "pay" ? "home" : tab;

  const marketMode = tab === "trade" ? "spot" : tab === "market" ? "prices" : "spot";

  return (
    <>
      <PwaUpdateBanner />
      <Shell
        title={TAB_TITLES[tab]}
        networkEnv={networkEnv}
        onNetworkEnvChange={setNetwork}
        sessionOn={sessionOn}
        navPad
        username={session?.username}
        onLogout={() => {
          logout(false);
          setTab("auth");
          toast.info("خروج");
        }}
      >
        {tab === "home" && <HomePanel onJump={go} />}
        {tab === "auth" && <AuthPanel onSuccess={() => setTab("wallets")} />}
        {tab === "wallets" && <WalletsPanel networkEnv={networkEnv} onJump={go} />}
        {tab === "shops" && <ShopsPanel networkEnv={networkEnv} />}
        {tab === "oracle" && <OraclePanel />}
        {tab === "pay" && <PayPanel networkEnv={networkEnv} />}
        {(tab === "market" || tab === "trade") && (
          <MarketPanel
            key="market-panel"
            initialMode={marketMode}
            requireAuthForTrade={!sessionOn}
            onNeedAuth={() => setTab("auth")}
          />
        )}
        {tab === "inbox" && <InboxPanel />}
      </Shell>
      <MobileNav tab={navTab === "auth" ? "home" : navTab} sessionOn={sessionOn} onChange={go} />
    </>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <SessionBridge />
    </ToastProvider>
  );
}

function SessionBridge() {
  const toast = useToast();
  return (
    <SessionProvider
      onExpired={() => {
        toast.info("سشن");
      }}
    >
      <AppInner />
    </SessionProvider>
  );
}
